import { queryOptions, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { usePlatform } from "@/hooks/usePlatform";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { useOnboardingContext } from "@/modules/onboarding/context";
import { SourceForm, type SourceFormData } from "@/modules/onboarding/forms/SourceForm/SourceForm";
import { storage } from "@/modules/storage";
import { getPlatformLocation } from "api/methods";
import Services from "services/Services";

const V3SignupSourcePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, auth, onOnboardingCompleted } = useOnboardingContext();
  const { platform } = usePlatform();
  const [hasAuthToken, setHasAuthToken] = useState<boolean | null>(null);

  useEffect(() => {
    let mounted = true;
    Services.auth
      .loadToken()
      .then((token) => {
        if (!mounted) return;
        setHasAuthToken(!!token);
      })
      .catch(() => {
        if (!mounted) return;
        setHasAuthToken(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const { data: platformLocation, isPending: isLoadingPlatformLocation } = useQuery(
    queryOptions({
      queryKey: getPlatformLocation.key(),
      queryFn: async () => {
        try {
          return await getPlatformLocation();
        } catch (err) {
          if (isAxiosError(err) && err.response?.status === 404) {
            return null;
          }
          throw err;
        }
      },
      enabled: hasAuthToken === true,
      staleTime: 0,
      retry: false,
    }),
  );

  useEffect(() => {
    if (hasAuthToken !== true) return;
    if (isLoadingPlatformLocation) return;
    if (platformLocation == null) {
      navigate("/signin/location", { replace: true });
    }
  }, [hasAuthToken, isLoadingPlatformLocation, navigate, platformLocation]);

  const referralSenderName = storage.getItem("referralSenderName");
  const initialSource = referralSenderName ? "friend" : (profile.signUpSource ?? "");
  const initialReferrerName = referralSenderName
    ? referralSenderName
    : (profile.referrerName ?? "");
  const [autoSubmitFailed, setAutoSubmitFailed] = useState(false);

  const isPreRegisteredWithSource = useMemo(
    () => auth.existingUser?.hasBeenPreRegistered && !!auth.existingUser.signUpSource,
    [auth.existingUser],
  );
  const isDisabled = isPreRegisteredWithSource && !autoSubmitFailed;
  const employeeNameReferralEnabled =
    platform?.platformSettings?.employeeNameReferralEnabled ?? false;

  const hasAutoSubmitted = useRef(false);
  useEffect(() => {
    if (isPreRegisteredWithSource && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      const signUpSource = auth.existingUser?.signUpSource;
      const referrerName =
        signUpSource === "employee" || signUpSource === "other" || signUpSource === "friend"
          ? (auth.existingUser?.referrerName ?? undefined)
          : undefined;

      onOnboardingCompleted({
        signUpSource: signUpSource ?? undefined,
        referrerName,
      }).then(() => {
        // onOnboardingCompleted swallows errors internally. On success it
        // navigates away (unmounting this component). If we're still mounted
        // here, the call failed — re-enable the form so the user can retry.
        setAutoSubmitFailed(true);
      });
    }
  }, [isPreRegisteredWithSource, auth.existingUser, onOnboardingCompleted]);

  const handleSubmit = useCallback(
    async ({ signUpSource, referrerName }: SourceFormData) => {
      const profileUpdates = {
        signUpSource,
        referrerName:
          signUpSource === "employee" || signUpSource === "other" || signUpSource === "friend"
            ? referrerName
            : undefined,
      };

      await onOnboardingCompleted(profileUpdates);
    },
    [onOnboardingCompleted],
  );

  return (
    <MobileViewportContainer header={<OnboardingBackButton />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col items-center"
      >
        <SourceForm
          key={`source-${initialSource}-${initialReferrerName}`}
          initialSource={initialSource}
          initialReferrerName={initialReferrerName}
          onSubmit={handleSubmit}
          disabled={isDisabled}
          employeeNameReferralEnabled={employeeNameReferralEnabled}
          submitButtonText={t("common.confirm")}
        />
      </motion.div>
    </MobileViewportContainer>
  );
};

export default V3SignupSourcePage;
